import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { FormAlert } from '../components/form';
import CurrencyViewToggle from '../components/CurrencyViewToggle';
import PillGroup from '../components/PillGroup';
import { hasMultimoneda } from '../utils/multimoneda';
import { formatMoney } from '../utils/money';
import {
  VISTA_CONSOLIDADO,
  displayCurrency,
  shouldShowCurrencyToggle,
  vistaHint,
} from '../utils/currencyView';
import useStickyMonedas from '../hooks/useStickyMonedas';
import { canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { scoreBadgeClasses } from '../utils/collectionScore';
import PlanUpgradeCard from '../components/PlanUpgradeCard';

const PERIODOS = [
  { id: 'semana', label: 'Semana' },
  { id: 'mes', label: 'Mes' },
  { id: 'trimestre', label: 'Trimestre' },
  { id: 'año', label: 'Año' },
  { id: 'todas', label: 'Todo' },
];

const ORDENES = [
  { id: 'total', label: 'Facturado' },
  { id: 'cobrado', label: 'Cobrado' },
  { id: 'pendiente', label: 'Pendiente' },
  { id: 'score', label: 'Score cobranza' },
  { id: 'margen', label: 'Margen est.' },
  { id: 'margenReal', label: 'Margen real' },
  { id: 'trabajos', label: 'Ventas' },
  { id: 'ultimo', label: 'Última venta' },
];

const PERIODO_TEXTO = {
  semana: 'la última semana',
  mes: 'este mes',
  trimestre: 'este trimestre',
  año: 'este año',
  todas: 'todo el historial',
};

function formatFecha(fecha) {
  if (!fecha) return '—';
  const s = String(fecha).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return new Date(y, m - 1, d).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

const ClientesDetalle = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [filtroFecha, setFiltroFecha] = useState('año');
  const [currencyVista, setCurrencyVista] = useState(VISTA_CONSOLIDADO);

  const currency = displayCurrency(currencyVista);
  const hasProfesional = canAccessProfesionalFeatures(user);

  const fetchClientes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch(
        `/api/invoices-dashboard/top-clientes?periodo=${filtroFecha}&limit=200&vista=${encodeURIComponent(currencyVista)}`
      );
      if (!res.ok) throw new Error('Error al cargar clientes');
      const data = await res.json();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('No se pudo cargar el ranking de clientes.');
    } finally {
      setLoading(false);
    }
  }, [token, filtroFecha, currencyVista, secureFetch]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const availableMonedas = useStickyMonedas(clientes, currencyVista);
  const showCurrencyToggle = hasMultimoneda(user) && shouldShowCurrencyToggle(availableMonedas);

  const filteredAndSortedClientes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = clientes.filter((cliente) => {
      const name = (cliente.cliente || '').toLowerCase();
      const pais = (cliente.pais || '').toLowerCase();
      return !q || name.includes(q) || pais.includes(q);
    });

    const valueOf = (c) => {
      switch (sortBy) {
        case 'trabajos':
          return c.cantidadTrabajos || 0;
        case 'ultimo':
          return c.ultimoTrabajo || '';
        case 'cobrado':
          return c.cobrado || 0;
        case 'pendiente':
          return c.pendiente || c.montoImpagas || 0;
        case 'score':
          return c.collectionScore ?? -1;
        case 'margen':
          return c.margenEstimado ?? -Infinity;
        case 'margenReal':
          return c.margenReal ?? -Infinity;
        default:
          return c.total || 0;
      }
    };

    return [...list].sort((a, b) => {
      const aValue = valueOf(a);
      const bValue = valueOf(b);
      if (typeof aValue === 'string') return String(bValue).localeCompare(String(aValue));
      return (bValue || 0) - (aValue || 0);
    });
  }, [clientes, searchTerm, sortBy]);

  const totalIngresos = filteredAndSortedClientes.reduce((sum, c) => sum + (c.total || 0), 0);
  const totalCobrado = filteredAndSortedClientes.reduce((sum, c) => sum + (c.cobrado || 0), 0);
  const totalPendiente = filteredAndSortedClientes.reduce((sum, c) => sum + (c.pendiente || c.montoImpagas || 0), 0);
  const totalClientes = filteredAndSortedClientes.length;
  const eficaciaCobro = totalIngresos > 0 ? (totalCobrado / totalIngresos) * 100 : 0;
  const topCliente = filteredAndSortedClientes[0];
  const topShare = totalIngresos > 0 && topCliente ? (topCliente.total / totalIngresos) * 100 : 0;
  const concentracion80 = useMemo(() => {
    if (!totalIngresos) return 0;
    let acc = 0;
    let n = 0;
    for (const c of filteredAndSortedClientes) {
      acc += c.total || 0;
      n += 1;
      if (acc >= totalIngresos * 0.8) return n;
    }
    return n;
  }, [filteredAndSortedClientes, totalIngresos]);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';

  const openCliente = (nombre) => {
    if (!nombre) return;
    navigate(`/facturas?cliente=${encodeURIComponent(nombre)}`);
  };

  return (
    <div className={modoOscuro ? 'w-full bg-black text-white' : 'w-full bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-4 sm:py-5 lg:py-6 pb-12 sm:pb-16 space-y-4 sm:space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/inicio')}
              className={`p-2 rounded-lg border shrink-0 ${modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              aria-label="Volver al inicio"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight ${textPri}`}>
                Mejores clientes
              </h1>
              <p className={`text-sm mt-1 ${textSec}`}>
                Ranking por facturado de {PERIODO_TEXTO[filtroFecha] || 'este período'}
                {currencyVista === VISTA_CONSOLIDADO && showCurrencyToggle ? ' · en pesos' : ''}
              </p>
            </div>
          </div>
          {showCurrencyToggle && (
            <div className="flex flex-col items-end gap-1">
              <CurrencyViewToggle
                value={currencyVista}
                onChange={setCurrencyVista}
                monedas={availableMonedas}
                modoOscuro={modoOscuro}
              />
              <p className={`text-[11px] ${textMuted}`}>{vistaHint(currencyVista)}</p>
            </div>
          )}
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {[
            { label: 'Clientes', value: loading ? '—' : String(totalClientes), hint: 'con ventas en el período' },
            { label: 'Facturado', value: loading ? '—' : formatMoney(totalIngresos, currency), hint: currencyVista === VISTA_CONSOLIDADO ? 'consolidado en pesos' : `solo ${currency}` },
            { label: 'Cobrado', value: loading ? '—' : formatMoney(totalCobrado, currency), hint: loading ? '' : `${eficaciaCobro.toFixed(0)}% de lo facturado` },
            { label: 'Pendiente', value: loading ? '—' : formatMoney(totalPendiente, currency), hint: 'saldo por cobrar' },
          ].map((card) => (
            <div key={card.label} className={`rounded-2xl border p-3.5 sm:p-4 ${box}`}>
              <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
              <div className={`text-lg sm:text-xl font-semibold mt-0.5 truncate ${textPri}`}>{card.value}</div>
              <div className={`text-[11px] mt-0.5 truncate ${textMuted}`}>{card.hint}</div>
            </div>
          ))}
        </div>

        {!hasProfesional && !loading && (
          <PlanUpgradeCard
            compact
            description="Margen real (egresos asignados), margen estimado y score de cobranza están en plan Profesional+."
            planLabel="Profesional"
            modoOscuro={modoOscuro}
          />
        )}

        {!loading && totalClientes > 1 && topCliente && (
          <div className={`rounded-2xl border px-4 py-3 text-sm ${box}`}>
            <span className={`font-semibold ${textPri}`}>{topCliente.cliente}</span>
            <span className={textSec}>
              {' '}concentra el {topShare.toFixed(1)}% de los ingresos.
              {concentracion80 > 0 && concentracion80 < totalClientes
                ? ` ${concentracion80} cliente${concentracion80 !== 1 ? 's' : ''} llegan al 80%.`
                : ''}
            </span>
          </div>
        )}

        <section className={`rounded-2xl border overflow-hidden ${box}`}>
          <div className={`p-4 sm:p-5 border-b space-y-3 ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <PillGroup value={filtroFecha} onChange={setFiltroFecha} options={PERIODOS} modoOscuro={modoOscuro} />
              <PillGroup value={sortBy} onChange={setSortBy} options={ORDENES} modoOscuro={modoOscuro} />
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por cliente o país"
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
              />
            </div>
          </div>

          <FormAlert type="error" className="mx-4 mt-4">{error}</FormAlert>

          {loading ? (
            <div className="p-4 sm:p-5 space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`h-16 rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
              ))}
            </div>
          ) : filteredAndSortedClientes.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <UserIcon className={`w-10 h-10 mx-auto mb-2 ${textMuted}`} />
              <p className={`text-sm font-medium ${textPri}`}>No hay clientes en este recorte</p>
              <p className={`text-xs mt-1 ${textMuted}`}>
                {searchTerm ? 'Probá otro nombre o país.' : 'Registrá ventas para armar el ranking.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead>
                  <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                    <th className="text-left font-semibold py-2.5 px-4 w-12">#</th>
                    <th className="text-left font-semibold py-2.5 px-3">Cliente</th>
                    <th className="text-left font-semibold py-2.5 px-3">Ventas</th>
                    <th className="text-left font-semibold py-2.5 px-3">Última</th>
                    <th className="text-left font-semibold py-2.5 px-3 min-w-[140px]">Participación</th>
                    {hasProfesional && (
                      <>
                        <th className="text-center font-semibold py-2.5 px-3">Score</th>
                        <th className="text-right font-semibold py-2.5 px-3" title="Con egresos asignados al cliente">Margen real</th>
                        <th className="text-right font-semibold py-2.5 px-3">Margen est.</th>
                      </>
                    )}
                    <th className="text-right font-semibold py-2.5 px-3">Cobrado</th>
                    <th className="text-right font-semibold py-2.5 px-4">Facturado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedClientes.map((cliente, index) => {
                    const porcentaje = totalIngresos > 0 ? (cliente.total / totalIngresos) * 100 : 0;
                    const nombre = cliente.cliente || 'Sin cliente';
                    const cobrado = cliente.cobrado || 0;
                    const pendiente = cliente.pendiente || cliente.montoImpagas || 0;
                    const eficacia = (cliente.total || 0) > 0 ? (cobrado / cliente.total) * 100 : 0;
                    return (
                      <tr
                        key={nombre}
                        onClick={() => openCliente(nombre)}
                        className={`cursor-pointer border-t transition-colors ${
                          modoOscuro
                            ? 'border-gray-800 hover:bg-gray-800/70'
                            : 'border-gray-100 hover:bg-gray-50'
                        }`}
                      >
                        <td className={`py-3 px-4 text-xs font-semibold ${textMuted}`}>{index + 1}</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                              modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                            }`}>
                              {initials(nombre)}
                            </div>
                            <div className="min-w-0">
                              <div className={`text-sm font-semibold truncate ${textPri}`}>{nombre}</div>
                              <div className={`text-[11px] truncate ${textMuted}`}>
                                {cliente.pais || 'Sin país'}
                                {cliente.impagas > 0 ? ` · ${cliente.impagas} impaga${cliente.impagas !== 1 ? 's' : ''}` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`py-3 px-3 text-sm ${textSec}`}>
                          <div>{cliente.cantidadTrabajos || 0}</div>
                          <div className={`text-[11px] ${textMuted}`}>
                            avg {formatMoney(cliente.ticketPromedio || 0, currency)}
                          </div>
                        </td>
                        <td className={`py-3 px-3 text-sm ${textSec}`}>
                          {formatFecha(cliente.ultimoTrabajo)}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
                              <div
                                className="h-full rounded-full bg-[#1F80FF]"
                                style={{ width: `${Math.min(Math.max(porcentaje, 0), 100)}%` }}
                              />
                            </div>
                            <span className={`text-[11px] font-semibold tabular-nums w-10 text-right ${textMuted}`}>
                              {porcentaje.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        {hasProfesional && (
                          <>
                            <td className="py-3 px-3 text-center">
                              {cliente.collectionScore != null ? (
                                <span
                                  className={`inline-flex flex-col items-center px-2 py-1 rounded-lg text-[10px] font-bold ${scoreBadgeClasses(cliente.collectionTone, modoOscuro)}`}
                                  title={cliente.brokenPromises ? `${cliente.brokenPromises} promesa(s) incumplida(s)` : undefined}
                                >
                                  <span>{cliente.collectionScore}</span>
                                  <span className="font-medium opacity-80">{cliente.collectionLabel}</span>
                                </span>
                              ) : (
                                <span className={textMuted}>—</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right">
                              {cliente.margenReal != null ? (
                                <>
                                  <div className={`text-sm font-semibold tabular-nums ${
                                    cliente.margenReal >= 0
                                      ? modoOscuro ? 'text-green-300' : 'text-green-700'
                                      : modoOscuro ? 'text-red-300' : 'text-red-700'
                                  }`}>
                                    {formatMoney(cliente.margenReal, currency)}
                                  </div>
                                  <div className={`text-[11px] ${textMuted}`}>
                                    {cliente.margenRealPct != null ? `${cliente.margenRealPct}%` : ''}
                                  </div>
                                </>
                              ) : (
                                <span className={`text-[11px] ${textMuted}`} title="Asigná egresos al cliente">—</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right">
                              {cliente.margenEstimado != null ? (
                                <>
                                  <div className={`text-sm font-semibold tabular-nums ${
                                    cliente.margenEstimado >= 0
                                      ? modoOscuro ? 'text-green-300' : 'text-green-700'
                                      : modoOscuro ? 'text-red-300' : 'text-red-700'
                                  }`}>
                                    {formatMoney(cliente.margenEstimado, currency)}
                                  </div>
                                  <div className={`text-[11px] ${textMuted}`}>
                                    {cliente.margenPct != null ? `${cliente.margenPct}%` : ''}
                                  </div>
                                </>
                              ) : (
                                <span className={textMuted}>—</span>
                              )}
                            </td>
                          </>
                        )}
                        <td className="py-3 px-3 text-right">
                          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>
                            {formatMoney(cobrado, currency)}
                          </div>
                          <div className={`text-[11px] ${textMuted}`}>
                            {eficacia.toFixed(0)}% cobrado
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>
                            {formatMoney(cliente.total, currency)}
                          </div>
                          {pendiente > 0.009 && (
                            <div className={`text-[11px] ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
                              {formatMoney(pendiente, currency)} pendiente
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {!loading && filteredAndSortedClientes.length > 0 && (
          <p className={`text-xs ${textMuted}`}>
            Tocá un cliente para ver sus ventas. Cobrado y pendiente usan el saldo, no todo o nada.
            {hasProfesional && ' El score de cobranza (Profesional+) combina historial de pago, atrasos y promesas incumplidas.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default ClientesDetalle;
